<template>
  <div id="content-type-management">
    <div id="title-panel">
      <div id="title-container">
        <h2>{{ t('projectDetails.contentTypes.contentTypes') }}</h2>
        <div id="content-types-counter">
          {{ contentTypes?.length ?? 0 }}
        </div>
      </div>
      <vf-button
        id="add-content-type-button"
        :size="buttonSizes.large"
        :variant="buttonVariants.secondary"
        @click="handleAddContentTypeButtonClick"
      >
        {{ t('projectDetails.contentTypes.addContentType') }}
      </vf-button>
    </div>

    <table-wrapper
      v-model:page-options="pageOptions"
      :fields="tableFields"
      :filter-display="false"
      :items="contentTypes"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      row-clickable
      @content-type-added="getContentTypes"
      @row-click="handleContentTypeEditIconClick"
    ></table-wrapper>

    <content-type-modal
      ref="modalRef"
      :mode="ProjectType.SUPPLIER_PROJECT"
      :project-id="projectId"
      :supplier-id="supplierId"
      @content-type-added="getContentTypes"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import axios, { AxiosResponse } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { IUploadCountForContentTypeName, ProjectType } from 'types';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import { useRouter } from 'vue-router';
  import ContentTypeModal from '../../components/ContentTypeModal/ContentTypeModal.vue';
  import {
    TableFieldsBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import { PROJECT_TYPE_PARAM, Route, routeParams } from '@/routes';

  const props = defineProps({
    projectId: {
      type: String,
      required: true,
    },
    supplierId: {
      type: String,
      required: true,
    },
  });

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const router = useRouter();

  const contentTypes = ref<IUploadCountForContentTypeName[]>([]);
  const TABLE_NAMESPACE = 'ctm';
  const { pageOptions, isLoading } =
    useTableWrapper<IUploadCountForContentTypeName>(
      TableMode.CLIENT_SIDE,
      'contentTypeName',
      getContentTypes,
      undefined,
      true,
      TABLE_NAMESPACE,
    );

  const tableFields = computed(() =>
    new TableFieldsBuilder<IUploadCountForContentTypeName>({
      sortable: true,
      labelTranslationKey: 'projectDetails.contentTypes',
    })
      .addField('contentTypeName', {
        width: '50%',
      })
      .addField('uploadCount', {
        width: '50%',
      })
      .build(),
  );

  async function getContentTypes() {
    try {
      const response: AxiosResponse<IUploadCountForContentTypeName[]> =
        await axios.get(
          endpointsList.projectsSuppliers.getContentTypesListForSupplierInProject(
            props.projectId,
            props.supplierId,
          ),
        );

      contentTypes.value = response.data;
    } catch {
      createErrorToast(t('errors.error'));
    }
  }

  function handleContentTypeEditIconClick(row: IUploadCountForContentTypeName) {
    router.push({
      name: Route.CONTENT_TYPE_CONFIGURATION,
      params: {
        [routeParams[Route.CONTENT_TYPE_CONFIGURATION].contentTypeId]:
          row.contentTypeId,
        [routeParams[Route.CONTENT_TYPE_CONFIGURATION].projectId]:
          props.projectId,
        [routeParams[Route.CONTENT_TYPE_CONFIGURATION][PROJECT_TYPE_PARAM]]:
          ProjectType.SUPPLIER_PROJECT,
        [routeParams[Route.CONTENT_TYPE_CONFIGURATION].supplierId]:
          props.supplierId,
      },
    });
  }

  const modalRef = ref<InstanceType<typeof ContentTypeModal> | null>(null);
  function handleAddContentTypeButtonClick() {
    modalRef.value?.show();
  }
</script>

<style scoped lang="scss">
  #content-type-management {
    margin-top: $tab-view-margin-top;

    #title-panel {
      margin-bottom: 20px;
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      align-items: center;
      justify-items: center;

      #title-container {
        justify-self: flex-start;
        display: flex;
        align-items: flex-start;
        column-gap: 10px;

        h2 {
          @include subheading;
        }

        #content-types-counter {
          @include heading-counter;
        }
      }

      button#add-content-type-button {
        justify-self: end;
        grid-column: 3/4;
      }
    }
  }
</style>
